'use client';

import type { CMSCard, CMSContentGroup, CMSContentItem } from '@/lib/cms/types';
import type { ContentfulCardFragment, ContentGroupFragment } from '@/lib/contentful/types';
import { filterCollection } from '@/lib/contentful/utils';
import { getMediaUrl } from '@/components/cms/Card/variants/MyAccount.utils';
import { isSvgAssetUrl } from '@/lib/cdn';
import { Link } from '@/lib/i18n/navigation';
import clsx from 'clsx';
import Image from 'next/image';
import styles from './MyAccountCardGroup.module.scss';

export interface MyAccountCardGroupProps {
  data: ContentGroupFragment | CMSContentGroup;
  extraData?: Record<string, unknown>;
  cmsIndex?: number;
  className?: string;
}

interface ParsedCard {
  id: string;
  mediaSrc: string | null;
  iconSrc?: string;
  iconContentType?: string | null;
  label: string;
  url?: string;
}

function stripHighlightMarkers(text: string): string {
  return text.replace(/\[\[|\]\]/g, '');
}

function parseCardData(item: ContentfulCardFragment | CMSContentItem, index: number): ParsedCard {
  const card = item as CMSCard;
  const sysId = (item as ContentfulCardFragment)?.sys?.id;

  const media = card.image || ('media' in item ? item.media : undefined);
  const icon = card.icon;

  const rawHeading =
    card.heading ||
    (card as unknown as { title?: { mainText?: string } }).title?.mainText ||
    (typeof card.heading === 'object' && card.heading && 'text' in card.heading
      ? (card.heading as { text?: string }).text
      : undefined);

  const label = rawHeading ? stripHighlightMarkers(rawHeading) : '';

  let url = card.cta?.url || ('url' in item && typeof item.url === 'string' ? item.url : undefined);
  if (url && url.endsWith('/my-account')) {
    url = url.replace(/\/my-account$/, '') || '/my-account';
  }

  return {
    id: sysId || `card-${index}`,
    mediaSrc: getMediaUrl(media),
    iconSrc: icon?.url,
    iconContentType: icon?.contentType,
    label,
    url,
  };
}

export function MyAccountCardGroup({ data, cmsIndex, className }: MyAccountCardGroupProps) {
  let items: (ContentfulCardFragment | CMSContentItem)[] = [];

  if ('itemsCollection' in data && data.itemsCollection) {
    items = filterCollection(data.itemsCollection);
  } else if ('items' in data && Array.isArray(data.items)) {
    items = data.items;
  }

  if (items.length === 0) {
    return null;
  }

  const cards = items.map((item, i) => parseCardData(item, i));
  const mdSpan = Math.floor(12 / cards.length);
  const colClass = styles[`mdCol${mdSpan}`] ?? '';

  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `myaccountcardgroup-${cmsIndex}-${element}`
        : `myaccountcardgroup-${cmsIndex}`
      : undefined;

  return (
    <div className={clsx(styles.wrapper, className)} data-testid={testId()}>
      <div className={styles.imageSection}>
        <div className={styles.grid}>
          {cards.map((card, index) => {
            const imageContent = (
              <div className={styles.imageCard}>
                <div className={styles.mediaContainer}>
                  {card.mediaSrc && (
                    <Image
                      src={card.mediaSrc}
                      // Decorative — the enclosing link's text label describes the destination
                      alt=""
                      aria-hidden="true"
                      fill
                      sizes="(max-width: 768px) 50vw, 25vw"
                      priority
                      className={styles.media}
                    />
                  )}
                  {card.iconSrc && (
                    <div className={styles.overlay}>
                      <div className={styles.iconWrapper}>
                        <Image
                          src={card.iconSrc}
                          alt=""
                          aria-hidden="true"
                          width={56}
                          height={56}
                          className={styles.icon}
                          unoptimized={isSvgAssetUrl(card.iconSrc, card.iconContentType)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );

            return (
              <div
                key={card.id}
                className={clsx(styles.column, colClass)}
                data-testid={testId(`column-${index}`)}
              >
                {card.url ? (
                  <Link href={card.url} className={styles.cardLink}>
                    {imageContent}
                    <p className={styles.label} data-testid={testId(`label-${index}`)}>
                      {card.label}
                    </p>
                  </Link>
                ) : (
                  <>
                    {imageContent}
                    <p className={styles.label} data-testid={testId(`label-${index}`)}>
                      {card.label}
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

MyAccountCardGroup.displayName = 'MyAccountCardGroup';
